import { Add, ArrowDownward, ArrowUpward, Link as LinkIcon, Remove } from '@mui/icons-material';
import { Stack, Typography, Box, TextField, Link, IconButton, Tooltip } from '@mui/material';
import { ReactNode, useState } from 'react';

type ObjectWithLinks = {
  links: { title: string; uri: string }[];
};

type ExternalLinksComponentProps<T extends ObjectWithLinks> = {
  objectWithLinks: T;
  setObjectsWithLinks: (objectWithLinks: T) => void;
  hideTitle?: boolean;
  copyButton?: ReactNode;
};

export const ExternalLinksComponent = <T extends ObjectWithLinks>(props: ExternalLinksComponentProps<T>) => {
  const [linkTitle, setLinkTitle] = useState<string>('');
  const [linkUri, setLinkUri] = useState<string>('');

  return (
    <Stack sx={{ mb: 2 }}>
      {!props.hideTitle && <Typography sx={{ mt: 1, mb: 2 }}>External Links</Typography>}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
        <TextField
          fullWidth
          label={'Uri'}
          value={linkUri}
          onChange={(e) => {
            setLinkUri(e.target.value);
          }}
        />
        <TextField
          label={'Title'}
          fullWidth
          value={linkTitle}
          onChange={(e) => {
            setLinkTitle(e.target.value);
          }}
        />
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Add link">
            <span>
              <IconButton
                aria-label="Add link"
                disabled={!linkTitle || !linkUri}
                onClick={() => {
                  props.setObjectsWithLinks({ ...props.objectWithLinks, links: [...(props.objectWithLinks.links || []), { title: linkTitle, uri: linkUri }] });
                  setLinkTitle('');
                  setLinkUri('');
                }}
              >
                <Add />
              </IconButton>
            </span>
          </Tooltip>
          {props.copyButton}
        </Stack>
      </Stack>
      {props.objectWithLinks.links?.map((el, index) => (
        <Box key={`${el.title}-${el.uri}-${index}`} sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LinkIcon fontSize="small" sx={{ color: 'common.white', flexShrink: 0 }} />
          <Link href={el.uri} sx={{ color: 'common.white', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {el.title}
          </Link>
          {props.objectWithLinks.links.length > 1 && (
            <>
              <Tooltip title="Move link up">
                <span>
                  <IconButton
                    aria-label={`Move ${el.title} up`}
                    disabled={index === 0}
                    onClick={() => {
                      const links = [...props.objectWithLinks.links];
                      [links[index - 1], links[index]] = [links[index], links[index - 1]];
                      props.setObjectsWithLinks({ ...props.objectWithLinks, links });
                    }}
                  >
                    <ArrowUpward />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move link down">
                <span>
                  <IconButton
                    aria-label={`Move ${el.title} down`}
                    disabled={index === props.objectWithLinks.links.length - 1}
                    onClick={() => {
                      const links = [...props.objectWithLinks.links];
                      [links[index], links[index + 1]] = [links[index + 1], links[index]];
                      props.setObjectsWithLinks({ ...props.objectWithLinks, links });
                    }}
                  >
                    <ArrowDownward />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title="Remove link">
            <IconButton
              aria-label={`Remove ${el.title}`}
              onClick={() => {
                props.setObjectsWithLinks({
                  ...props.objectWithLinks,
                  links: props.objectWithLinks.links.filter((_, linkIndex) => linkIndex !== index),
                });
              }}
            >
              <Remove />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Stack>
  );
};
